/**
 * @file Данные скачивания и проверки ответов диапазонов.
 *
 * end в локальном диапазоне не включён; HTTP Range передаёт end - 1.
 * Длина открытого файла и длина зашифрованного тела — разные величины.
 */
import { withAbort } from '../../services/abort.ts'

export const DOWNLOAD_CHUNK_SIZE = 512 * 1024
export const DOWNLOAD_WINDOW = 4
export const MEMORY_SAVE_LIMIT = 64 * 1024 ** 2

export type DownloadErrorCode =
    | 'quota'
    | 'quotaCheck'
    | 'network'
    | 'rateLimited'
    | 'access'
    | 'notFound'
    | 'protocol'
    | 'incomplete'
    | 'changed'
    | 'decryption'
    | 'write'
    | 'saveTimeout'
    | 'memoryLimit'
    | 'archiveFailed'
    | 'archiveUnknown'

export class DownloadError extends Error {
    readonly code: DownloadErrorCode

    constructor(code: DownloadErrorCode) {
        super(code)
        this.name = 'DownloadError'
        this.code = code
    }
}

export function asDownloadError(error: unknown): DownloadError {
    return error instanceof DownloadError ? error : new DownloadError('network')
}

export type DownloadPhase =
    | 'preparing'
    | 'checking'
    | 'downloading'
    | 'saving'
    | 'cancelling'
    | 'succeeded'
    | 'failed'
    | 'cancelled'

export interface DownloadRequest {
    kind: 'file' | 'folder'
    name: string
    path: string
    boxId: string
    token: string
    password: string
}

export interface DownloadSource {
    path: string
    name: string
    temporary: boolean
}

export interface DownloadMetadata {
    size: number
}

export interface DownloadRange {
    start: number
    end: number
}

export interface DownloadWriter {
    write: (data: Uint8Array<ArrayBuffer>) => Promise<void>
    close: () => Promise<void>
    abort: (reason?: unknown) => Promise<void>
}

export interface DownloadState {
    id: string
    boxId: string
    sourcePath: string
    name: string
    kind: 'file' | 'folder'
    size: number | null
    written: number
    phase: DownloadPhase
    error: DownloadErrorCode | null
    cleanupWarning: boolean
}

export function isDownloadBusy(state: DownloadState | null): boolean {
    return (
        state !== null &&
        !['succeeded', 'failed', 'cancelled'].includes(state.phase)
    )
}

export function downloadPercent(state: DownloadState): number {
    if (state.phase === 'succeeded') return 100

    return !state.size
        ? 0
        : Math.min(99, Math.floor((state.written / state.size) * 100))
}

export function checkDownloadResponse(response: Response): void {
    if (response.ok) return

    if (response.status === 401 || response.status === 403)
        throw new DownloadError('access')

    if (response.status === 404) throw new DownloadError('notFound')

    if (response.status === 416) throw new DownloadError('changed')

    if (response.status === 429) throw new DownloadError('rateLimited')

    if (response.status >= 500 || response.status === 408)
        throw new DownloadError('network')

    throw new DownloadError('protocol')
}

export function parseDownloadMetadata(response: Response): DownloadMetadata {
    checkDownloadResponse(response)
    const length = response.headers.get('x-content-length')
    if (
        length === null ||
        !/^\d+$/.test(length) ||
        !Number.isSafeInteger(Number(length))
    )
        throw new DownloadError('protocol')

    return {
        size: Number(length),
    }
}

export function verifyMetadata(
    before: DownloadMetadata,
    after: DownloadMetadata,
): void {
    if (before.size !== after.size) throw new DownloadError('changed')
}

export function verifyRangeResponse(
    response: Response,
    expectedBodySize: number,
): void {
    // Квотируется получение данных файла, а не HEAD или подготовка архива.
    if (response.status === 402) throw new DownloadError('quota')

    checkDownloadResponse(response)
    if (response.status !== 200 && response.status !== 206)
        throw new DownloadError('protocol')

    // GET сообщает длину переданного тела. Для шифрованного чанка она больше
    // открытого диапазона на salt и padding; фактические байты проверит reader.
    const length = response.headers.get('X-Content-Length')
    if (
        length === null ||
        !/^\d+$/.test(length) ||
        !Number.isSafeInteger(Number(length))
    )
        throw new DownloadError('protocol')

    if (Number(length) !== expectedBodySize)
        throw new DownloadError('incomplete')
}

/** Читает только ожидаемый объём; лишние или недостающие байты — ошибка передачи. */
export async function readBoundedBody(
    response: Response,
    expected: number,
    signal: AbortSignal,
): Promise<Uint8Array<ArrayBuffer>> {
    if (!response.body) throw new DownloadError('incomplete')

    const reader = response.body.getReader()
    const chunks: Uint8Array<ArrayBuffer>[] = []
    let size = 0

    const abort = () => {
        void reader.cancel(signal.reason).catch(() => {})
    }

    signal.addEventListener('abort', abort, { once: true })

    try {
        while (true) {
            signal.throwIfAborted()
            const { value, done } = await withAbort(reader.read(), signal)
            signal.throwIfAborted()
            if (done) break

            size += value.byteLength
            if (size > expected) throw new DownloadError('incomplete')

            chunks.push(value)
        }
        if (size !== expected) throw new DownloadError('incomplete')

        const result = new Uint8Array(size)
        let offset = 0

        for (const chunk of chunks) {
            result.set(chunk, offset)
            offset += chunk.byteLength
        }

        return result
    } finally {
        signal.removeEventListener('abort', abort)
        void reader.cancel().catch(() => {})
        reader.releaseLock()
    }
}
